import { createGetRequest } from 'app/selectors';
import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { suppressKeyboardEventForInput } from 'components/AgGrid/utils';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimActKc2OtherItemData, BimWorkOrderOtherPriceData, ProjectStatusEnum } from 'gql/graphql';
import { BimActKc2OtherStatusEnum, ContractStatusEnum, DeviationEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { useTimeout } from 'hooks/useTimeout';
import {
    bimNumberFormatter,
    percentCellFormatter,
    statusRenderer,
} from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers';
import { useScrolledToActId } from 'pages/Project/Common/hooks/useScrolledToActId';
import type { ContextMenuItemDefault } from 'uikit';
import { formatNumberToRu } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { cellWithDifferentFiled } from '../../BimWorkOrderPriceTable/cellRenderers';
import { getActsKc2OtherCostAcceptedKey, getActsKc2OtherCostCommentKey, getActsKc2OtherCostPlanKey } from '../../types';
import { ActHeaderComponent } from '../components/ActHeaderComponent';
import { ActKC2OtherCostCommentCellRendererComponent } from '../components/ActKC2OtherCostCommentCellRendererComponent';
import { ActsKc2OtherCostCellRendererComponent } from '../components/ActsKc2OtherCostCellRendererComponent';
import { ActsTotalHeaderComponent } from '../components/ActsTotalHeaderComponent';

import { useWorkOrderOtherMenuColumn } from './useWorkOrderOtherMenuColumn';

interface IUseWorkOrderOtherColumns {
    constructionTypeId?: number;
    totalDetailsRowOpenOuter: boolean;
    statusWorkOrder: WorkOrderStatusEnum;
    hasInProgress: boolean;
    refreshWorkOrderOtherPriceList: () => void;
    refreshBimActsKC2Other: () => void;
    toggleTotalDetailsOuter: () => void;
    menuItemsGetter: (
        actKc2Other: BimActKc2OtherItemData,
        aktKC2BimOtherRules: IUseAccess['aktKC2BimOther'],
    ) => ContextMenuItemDefault[];
    isFrameContract: boolean;
    changeFilterHandler?: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] }) => void;
    nameFilterItems?: { label: string }[] | undefined;
    actsKc2Other?: BimActKc2OtherItemData[] | null;
    contractStatus?: ContractStatusEnum;
    hasExcessCompletedOthers?: boolean;
    orderRules?: IUseAccess['orderBim'];
    statusProject?: ProjectStatusEnum;
    othersPriceDataStatus?: RequestStatus;
}

const rightAlignedCellClassName = 'ag-right-aligned-cell';
const rightAlignedBoldCellClassName = 'ag-right-aligned-cell boldWeight';
const bgBlueClassName = 'ag-cell-bg-blue';

export const useWorkOrderOtherColumns = (props: IUseWorkOrderOtherColumns) => {
    const {
        statusWorkOrder,
        constructionTypeId,
        totalDetailsRowOpenOuter,
        toggleTotalDetailsOuter,
        refreshWorkOrderOtherPriceList,
        actsKc2Other,
        menuItemsGetter,
        isFrameContract,
        changeFilterHandler,
        nameFilterItems,
        contractStatus,
        hasExcessCompletedOthers,
        orderRules,
        hasInProgress,
        statusProject,
        othersPriceDataStatus,
    } = props;

    const disabledByContractStatus = useMemo(
        () => contractStatus === ContractStatusEnum.Delayed || contractStatus === ContractStatusEnum.Completed,
        [contractStatus],
    );

    const [selection, , , setSelection] = useSelection('bimWorkOrderOtherPriceTable');
    const [pinnedItems, , , setPinned] = usePin('bimWorkOrderOtherPriceTable');

    const [isClassColor, setIsClassColor] = useState<boolean>(false);
    const [isCheckDisabled, setIsCheckDisabled] = useState<boolean>(false);

    const requestSelector = useMemo(() => createGetRequest('bimUpdateActKC2Other'), []);

    const bimUpdateActKC2Other = useSelector(requestSelector);

    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        if (bimUpdateActKC2Other?.data?.bimCreateActKC2Other?.result) {
            setIsClassColor(true);
            setIsExpanded(true);
        }
    }, [bimUpdateActKC2Other?.data?.bimCreateActKC2Other?.result]);

    useTimeout(() => setIsClassColor(false), isClassColor ? 16000 : null);

    const {
        menuColumn,
        checkedRowsArr,
        resetCheckedRows,
        isSingleDeleteModalOpened,
        closeSingleDeleteModalHandler,
        totalDetailsOpened,
        toggleTotalDetails,
    } = useWorkOrderOtherMenuColumn({
        constructionTypeId,
        statusWorkOrder,
        refreshWorkOrderOtherPriceList,
        isFrameContract,
        contractStatus,
        isCheckDisabled,
        orderRules,
    });

    const onUpdateFinish = useCallback(
        (isSuccess: boolean) => {
            if (isSuccess) {
                refreshWorkOrderOtherPriceList();
            }

            setIsCheckDisabled(false);
        },
        [refreshWorkOrderOtherPriceList],
    );

    const onUpdateStart = useCallback(() => {
        setIsCheckDisabled(true);
    }, []);

    const othersPriceDataSuccess = othersPriceDataStatus === RequestStatus.SUCCESS;

    const { scrolledToActId } = useScrolledToActId();

    const actsOtherColumns = useMemo(
        () =>
            [...(actsKc2Other ?? [])]
                .sort((a, b) => b.id - a.id)
                .map((act, i) => {
                    const changeClassName =
                        othersPriceDataSuccess && isClassColor && i === 0
                            ? `${bgBlueClassName} ${rightAlignedCellClassName} ${act.isInactive ? 'inactive-act' : ''}`
                            : `${rightAlignedCellClassName} ${act.isInactive ? 'inactive-act' : ''}`;

                    const isBlue = scrolledToActId === act.id || (othersPriceDataSuccess && isClassColor && i === 0);

                    return {
                        id: act.id,
                        field: `actOther${act.id}`,
                        spanHeaderHeight: true,
                        headerGroupComponent: ActHeaderComponent,
                        headerGroupComponentParams: {
                            actKc2Other: act,
                            menuItemsGetter,
                        },
                        headerClass: isBlue ? bgBlueClassName : null,
                        children: [
                            {
                                headerName: 'Стоимость (План)',
                                field: getActsKc2OtherCostPlanKey(act.id),
                                valueFormatter: bimNumberFormatter(0),
                                hide: !isExpanded || act.status === BimActKc2OtherStatusEnum.Accepted,
                                headerClass: isBlue ? bgBlueClassName : null,
                                cellClass: isBlue ? bgBlueClassName : changeClassName,
                                cellRenderer: ActsKc2OtherCostCellRendererComponent,
                                cellRendererParams: {
                                    columnKey: getActsKc2OtherCostPlanKey(act.id),
                                    isEditable:
                                        act.status === BimActKc2OtherStatusEnum.InProgress &&
                                        !disabledByContractStatus &&
                                        statusWorkOrder === WorkOrderStatusEnum.InProgress,
                                    isPlanCost: true,
                                    actKC2OtherId: act.id,
                                    onUpdateFinish,
                                    onUpdateStart,
                                    statusProject,
                                },
                                suppressKeyboardEvent: suppressKeyboardEventForInput,
                            },
                            {
                                headerName: 'Стоимость (Согласовано)',
                                field: getActsKc2OtherCostAcceptedKey(act.id),
                                valueFormatter: bimNumberFormatter(0),
                                hide: !isExpanded,
                                headerClass: isBlue ? bgBlueClassName : null,
                                cellClass: isBlue ? bgBlueClassName : changeClassName,
                                cellRenderer: ActsKc2OtherCostCellRendererComponent,
                                cellRendererParams: {
                                    columnKey: getActsKc2OtherCostAcceptedKey(act.id),
                                    isEditable:
                                        act.status === BimActKc2OtherStatusEnum.OnCheck && !disabledByContractStatus,
                                    isPlanCost: false,
                                    actKC2OtherId: act.id,
                                    onUpdateFinish,
                                    onUpdateStart,
                                    statusProject,
                                },
                                suppressKeyboardEvent: suppressKeyboardEventForInput,
                            },
                            {
                                headerName: 'Комментарий',
                                field: getActsKc2OtherCostCommentKey(act.id),
                                hide: !isExpanded,
                                headerClass: isBlue ? bgBlueClassName : null,
                                // eslint-disable-next-line no-nested-ternary
                                cellClass: isBlue ? bgBlueClassName : act.isInactive ? 'inactive-act' : '',
                                cellRenderer: ActKC2OtherCostCommentCellRendererComponent,
                                cellRendererParams: {
                                    actKc2Other: act,
                                    statusProject,
                                    isInactive: act.isInactive,
                                    onUpdateFinish,
                                },
                                tooltipComponent: AgGridCellTooltipComponent,
                                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                            },
                        ],
                    };
                }),
        [
            actsKc2Other,
            disabledByContractStatus,
            isClassColor,
            isExpanded,
            menuItemsGetter,
            onUpdateFinish,
            onUpdateStart,
            othersPriceDataSuccess,
            scrolledToActId,
            statusProject,
            statusWorkOrder,
        ],
    );

    const actsOtherColumnsDetailsFieldsNames = useMemo(
        () =>
            actsOtherColumns.reduce((acc: string[], item) => {
                acc = [...acc, ...item.children.map((c) => c.field)];

                return acc;
            }, []),
        [actsOtherColumns],
    );

    const inProgressColumns = useMemo<ColDef<BimWorkOrderOtherPriceData>[]>(
        () => [
            {
                headerName: 'Кол-во (объем)',
                field: 'calculatingTypeValue',
                minWidth: 100,
                spanHeaderHeight: true,
                valueFormatter: percentCellFormatter,
            },
            {
                headerName: `План по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                spanHeaderHeight: true,
                marryChildren: true,
                children: [
                    {
                        headerName: 'Прочие затраты',
                        field: 'planValueOthers',
                        minWidth: 115,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                        cellRenderer: cellWithDifferentFiled('planValueOthers', 'planValue.others'),
                        cellClass: rightAlignedCellClassName,
                    },
                    {
                        headerName: 'Всего',
                        field: 'planValueTotal',
                        minWidth: 115,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                        cellRenderer: cellWithDifferentFiled('planValueTotal', 'planValue.total'),
                        cellClass: rightAlignedCellClassName,
                    },
                ],
            },
            {
                headerName: `Выполнено по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                marryChildren: true,
                children: [
                    {
                        headerName: 'Прочие затраты',
                        children: [
                            {
                                headerName: 'Итого по Актам',
                                field: 'completedValueOthers',
                                spanHeaderHeight: true,
                                valueFormatter: bimNumberFormatter(0),
                                cellRenderer: cellWithDifferentFiled(
                                    'completedValueOthers',
                                    'completedValue.others',
                                    (data) => {
                                        if (data.completedOtherDeviation === DeviationEnum.Up) {
                                            return (
                                                <span style={{ color: 'var(--color-typo-alert)', marginRight: 5 }}>
                                                    ▲
                                                </span>
                                            );
                                        }

                                        return undefined;
                                    },
                                ),
                                headerComponent: ActsTotalHeaderComponent,
                                headerComponentParams: {
                                    collapseKeys: actsOtherColumnsDetailsFieldsNames,
                                    isExpanded,
                                    setIsExpanded,
                                    hasExcessCompletedOthers,
                                },
                                cellClass: rightAlignedCellClassName,
                            },
                            ...actsOtherColumns,
                        ],
                    },
                    {
                        headerName: 'Всего',
                        field: 'completedValueTotal',
                        width: 115,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                        cellRenderer: cellWithDifferentFiled('completedValueTotal', 'completedValue.total'),
                        cellClass: rightAlignedCellClassName,
                    },
                ],
            },
            {
                headerName: `Остаток по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                spanHeaderHeight: true,
                marryChildren: true,
                children: [
                    {
                        headerName: 'Прочие затраты',
                        field: 'remainderValueOthers',
                        width: 115,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                        cellRenderer: cellWithDifferentFiled('remainderValueOthers', 'remainderValue.others'),
                        cellClass: rightAlignedCellClassName,
                    },
                    {
                        headerName: 'Всего',
                        field: 'remainderValueTotal',
                        width: 115,
                        spanHeaderHeight: true,
                        valueFormatter: bimNumberFormatter(0),
                        cellRenderer: cellWithDifferentFiled('remainderValueTotal', 'remainderValue.total'),
                        cellClass: rightAlignedCellClassName,
                    },
                ],
            },
        ],
        [actsOtherColumns, actsOtherColumnsDetailsFieldsNames, hasExcessCompletedOthers, isExpanded, isFrameContract],
    );

    const onFormationColumns = useMemo<ColDef<BimWorkOrderOtherPriceData>[]>(
        () => [
            {
                headerName: 'Кол-во (объем)',
                field: 'calculatingTypeValue',
                flex: 1,
                minWidth: 150,
                maxWidth: 200,
                valueFormatter: percentCellFormatter,
            },
            {
                headerName: 'Прочие затраты',
                field: 'planValueOthers' as any,
                flex: 1,
                minWidth: 180,
                valueFormatter: bimNumberFormatter(0),
                cellRenderer: cellWithDifferentFiled('planValueOthers', 'planValue.others'),
                cellClass: rightAlignedCellClassName,
            },
            {
                headerName: 'Всего',
                field: 'planValueTotal' as any,
                flex: 1,
                minWidth: 150,
                maxWidth: 200,
                valueFormatter: bimNumberFormatter(0),
                cellRenderer: cellWithDifferentFiled('planValueTotal', 'planValue.total'),
                cellClass: rightAlignedCellClassName,
            },
        ],
        [],
    );

    const tableColumns = useMemo<(ColDef<BimWorkOrderOtherPriceData> | ColGroupDef<BimWorkOrderOtherPriceData>)[]>(
        () => [
            menuColumn,
            {
                headerName: 'Шифр',
                field: 'valueCode' as any,
                width: 240,
                spanHeaderHeight: true,
                cellRenderer: statusRenderer,
                lockPosition: 'left',
            },
            {
                headerName: 'Наименование работ, затрат',
                field: 'name',
                flex: 1,
                minWidth: 400,
                lockPosition: 'left',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    changeFilterHandler,
                    filterItems: nameFilterItems,
                    columns: [
                        {
                            headerName: 'Наименование работ, затрат',
                            field: 'label',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    isClientSideSearch: true,
                    childFieldAccessor: 'label',
                },
            },
            ...(statusWorkOrder === WorkOrderStatusEnum.InProgress ||
            statusWorkOrder === WorkOrderStatusEnum.Completed ||
            (statusWorkOrder === WorkOrderStatusEnum.Delayed && hasInProgress)
                ? inProgressColumns
                : onFormationColumns),
        ],
        [
            changeFilterHandler,
            hasInProgress,
            inProgressColumns,
            menuColumn,
            nameFilterItems,
            onFormationColumns,
            statusWorkOrder,
        ],
    );

    const settingsColumns = useMemo(
        () =>
            tableColumns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean; isPinDefDisabled: boolean };

                    if (idx < 3) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [pinnedItems, tableColumns],
    );

    const isOtherColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns: tableColumns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isOtherColumnVisible(getColumnId(column)));
    }, [isOtherColumnVisible, pinnedItems, tableColumns]);

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                setSelection,
                setPinned,
                settingColumns: settingsColumns,
                incomingColumns: visible,
            });
        },
        [setSelection, setPinned, settingsColumns],
    );

    const closeTotalDetails = useCallback(() => {
        if (totalDetailsRowOpenOuter) {
            toggleTotalDetailsOuter();
        } else {
            toggleTotalDetails();
        }
    }, [toggleTotalDetails, toggleTotalDetailsOuter, totalDetailsRowOpenOuter]);

    const detailsColumns = useMemo<ColDef[]>(
        () => [
            {
                headerName: 'Наименование работ, затрат',
                field: 'name',
                flex: 1,
                minWidth: 250,
                spanHeaderHeight: true,
                lockPosition: 'left',
            },
            ...(statusWorkOrder === WorkOrderStatusEnum.InProgress ||
            statusWorkOrder === WorkOrderStatusEnum.Completed ||
            (statusWorkOrder === WorkOrderStatusEnum.Delayed && hasInProgress)
                ? [
                      {
                          ...inProgressColumns[1],
                          children: (inProgressColumns[1] as ColGroupDef)?.children?.map((c) => ({
                              ...c,
                              cellClass: rightAlignedBoldCellClassName,
                          })),
                      },
                      {
                          headerName: `Выполнено по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                          field: 'completedValue.total',
                          width: 200,
                          spanHeaderHeight: true,
                          cellClass: rightAlignedBoldCellClassName,
                          cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 0) || '',
                      },
                      {
                          headerName: `Остаток по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                          field: 'remainderValue.total',
                          width: 200,
                          spanHeaderHeight: true,
                          cellClass: rightAlignedBoldCellClassName,
                          cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 0) || '',
                      },
                  ]
                : onFormationColumns.slice(1).map((c) => ({ ...c, cellClass: rightAlignedBoldCellClassName }))),
        ],
        [hasInProgress, inProgressColumns, isFrameContract, onFormationColumns, statusWorkOrder],
    );

    const outerExpandOpener = useCallback(() => {
        setIsExpanded(true);
    }, []);

    return {
        fullSetColumns,
        detailsColumns,
        settingsColumns,
        isOtherColumnVisible,
        handleTableSettingsConfirm,
        checkedRowsArr,
        resetCheckedRows,
        isSingleDeleteModalOpened,
        closeSingleDeleteModalHandler,
        closeTotalDetails,
        totalDetailsOpened,
        outerExpandOpener,
    };
};
